import { api } from "../../api/index.ts";
import { useCallback } from "react";
import { ForwardingForm } from "../../types/forwarding.ts";
import {
  forwardingOrderUpdateToExternal,
  forwardingOrderCreateToExternal,
} from "./convert.ts";
import { useAppDispatch } from "../../redux/store.ts";
import { useView } from "../../contexts/ViewContext.tsx";
import { loadingCellsSlice } from "../../redux/slices/loadingCells.ts";
import { extractApiError } from "../../api/errors.ts";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { useShowToast } from "../../contexts/ToastContext.tsx";

export const useSetForwardingOrderData = () => {
  const view = useView();
  const showToast = useShowToast();
  const dispatch = useAppDispatch();
  const [triggerCreate] =
    api.endpoints.createForwardingOrderApiForwardingOrdersPost.useMutation();
  const [triggerUpdate] =
    api.endpoints.updateForwardingOrderApiForwardingOrdersForwardingOrderIdPut.useMutation();
  const [triggerDelete] =
    api.endpoints.deleteForwardingOrderApiForwardingOrdersForwardingOrderIdDelete.useMutation();

  const setData = useCallback(
    async (updatedRows: Partial<ForwardingForm>[]) => {
      const loadingCellKeys = updatedRows
        .filter((row) => !!row.id)
        .map((row) =>
          Object.entries(row).reduce(
            (acc, [key]) => [...acc, `${view}.${row.id}.${key}`],
            [] as string[],
          ),
        )
        .flat();
      loadingCellKeys.forEach((key) => {
        dispatch(loadingCellsSlice.actions.cellIsPending({ cell: key }));
      });
      return await Promise.all(
        updatedRows.map((row) => {
          if (row.id) {
            const update = forwardingOrderUpdateToExternal(row);
            if (Object.entries(update).length !== 0) {
              return triggerUpdate({
                forwardingOrderId: row.id,
                forwardingOrderUpdate: update,
              });
            } else {
              return undefined;
            }
          } else {
            return triggerCreate({
              forwardingOrderCreate: forwardingOrderCreateToExternal(
                // TODO: Provide fallback default values to ensure this has full set of values
                row as ForwardingForm,
              ),
            });
          }
        }),
      )
        .then((results) => {
          console.info("Update results ", results);
          results.forEach((result) => {
            if (result && "error" in result) {
              showToast({
                severity: "error",
                summary: "Błąd",
                detail: extractApiError(
                  result.error as FetchBaseQueryError | SerializedError,
                ),
              });
            }
          });
          return results;
        })
        .catch((err) => {
          console.error(err);
          return null;
        })
        .finally(() => {
          const runningQueries = dispatch(
            api.util.getRunningQueriesThunk(),
          ).filter((query) =>
            query.queryCacheKey.startsWith(
              "listForwardingOrdersApiForwardingOrdersGet",
            ),
          );
          // Wait for the latest running query for list endpoint to finish, this avoids "flickering"
          void Promise.all(runningQueries).finally(() => {
            loadingCellKeys.forEach((key) => {
              dispatch(loadingCellsSlice.actions.cellIsLoaded({ cell: key }));
            });
          });
        });
    },
    [triggerCreate, triggerUpdate, dispatch, view, showToast],
  );

  const deleteData = useCallback(
    async (forwardingOrderId: number) => {
      try {
        const result = await triggerDelete({ forwardingOrderId });
        console.info("Deleted forwarding order", forwardingOrderId, result);
        return result;
      } catch (error) {
        console.error("Delete error", error);
        throw error;
      }
    },
    [triggerDelete],
  );

  return { setData, deleteData };
};
